import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { User } from 'firebase/auth';
import { FirebaseAuthService } from '../../../../shared/firebase-auth.service';
import { RbacService } from '../../../../shared/rbac.service';
import { ClientLoggingService } from '../../../../shared/client-logging.service';

/**
 * Shared Account & app menu component used across top app bars and dialog headers (e.g. project selector).
 * Provides access to current account identity, account switching, billing navigation, admin console (if authorized),
 * sign out, and legal links.
 */
@Component({
  selector: 'app-account-menu',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
  ],
  templateUrl: './account-menu.component.html',
  styleUrls: ['./account-menu.component.scss'],
})
export class AccountMenuComponent implements OnInit {
  private router = inject(Router);
  private firebaseAuthService = inject(FirebaseAuthService);
  private rbacService = inject(RbacService);
  private dialogRef = inject(MatDialogRef, { optional: true });

  @Input() user$?: Observable<User | null>;
  @Input() userRoleLabel: string = 'Logged in as';
  @Input() hideAdminConsoleLink: boolean = false;
  @Input() showBilling: boolean = true;
  @Input() showSwitchAccount: boolean = true;
  @Input() showLegalLinks: boolean = true;
  @Input() ariaLabel: string = 'Account & app menu';
  @Input() tooltip: string = 'Account & app menu';

  @Output() signOut = new EventEmitter<void>();
  @Output() switchAccount = new EventEmitter<void>();
  @Output() navigateToBilling = new EventEmitter<void>();
  @Output() navigateToAdmin = new EventEmitter<void>();

  hasAdminRole$: Observable<boolean> = of(false);

  ngOnInit(): void {
    if (!this.user$) {
      this.user$ = this.firebaseAuthService.user$;
    }

    this.hasAdminRole$ = this.user$.pipe(
      switchMap(user => {
        if (!user || this.hideAdminConsoleLink) {
          return of(false);
        }
        return this.rbacService.checkAdminRole('', 'root').pipe(
          map(response => response?.hasAdminRole || false),
          catchError(error => {
            ClientLoggingService.log('WARN', `[AccountMenu] Error checking admin role for ${user.email}:`, error);
            return of(false);
          })
        );
      })
    );
  }

  onSwitchAccount(): void {
    if (this.switchAccount.observed) {
      this.switchAccount.emit();
    } else {
      void this.firebaseAuthService.signIn(true);
    }
  }

  onNavigateToBilling(): void {
    if (this.navigateToBilling.observed) {
      this.navigateToBilling.emit();
      return;
    }
    if (this.dialogRef) {
      this.dialogRef.close({ action: 'billing' });
    }
    ClientLoggingService.log('INFO', '[AccountMenu] Navigating to billing page');
    void this.router.navigate(['/billing']);
  }

  onNavigateToAdmin(): void {
    if (this.navigateToAdmin.observed) {
      this.navigateToAdmin.emit();
      return;
    }
    if (this.dialogRef) {
      this.dialogRef.close({ action: 'admin' });
    }
    ClientLoggingService.log('INFO', '[AccountMenu] Navigating to admin console');
    void this.router.navigate(['/admin']);
  }

  onSignOut(): void {
    if (this.signOut.observed) {
      this.signOut.emit();
      return;
    }
    if (this.dialogRef) {
      this.dialogRef.close({ action: 'sign-out' });
    }
    void this.firebaseAuthService.signOut();
  }

  onLegalLinkClick(): void {
    if (this.dialogRef) {
      this.dialogRef.close({ action: 'legal' });
    }
  }
}
